const CMS_API_BASE = process.env.REACT_APP_CMS_API_BASE || 'https://kwikfill-cms-api.burcs.workers.dev';
// Write endpoints go through /api/cms/admin/* (protected by Cloudflare Access)
const CMS_ADMIN = `${CMS_API_BASE}/api/cms/admin`;
// Read endpoints go through /api/cms/* (public, no Access required)
const CMS_PUBLIC = `${CMS_API_BASE}/api/cms`;

// Cloudflare Access service token — allows API calls to bypass the Access login page
// The email-based Access auth is enforced via the ?editmode redirect flow
const CF_ACCESS_CLIENT_ID = process.env.REACT_APP_CF_ACCESS_CLIENT_ID || '';
const CF_ACCESS_CLIENT_SECRET = process.env.REACT_APP_CF_ACCESS_CLIENT_SECRET || '';

const CMS_TOKEN_KEY = 'cms_admin_token';

export function getCmsToken(): string | null {
    return localStorage.getItem(CMS_TOKEN_KEY);
}

export function setCmsToken(token: string): void {
    localStorage.setItem(CMS_TOKEN_KEY, token);
}

export function clearCmsToken(): void {
    localStorage.removeItem(CMS_TOKEN_KEY);
}

function adminHeaders(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getCmsToken();
    if (token) h['x-cms-token'] = token;
    if (CF_ACCESS_CLIENT_ID) h['CF-Access-Client-Id'] = CF_ACCESS_CLIENT_ID;
    if (CF_ACCESS_CLIENT_SECRET) h['CF-Access-Client-Secret'] = CF_ACCESS_CLIENT_SECRET;
    return h;
}

// --- Read (public — no Access, no auth) ---

export async function fetchPageContent(page: string, section?: string): Promise<Record<string, Record<string, string>>> {
    const params = new URLSearchParams({ page });
    if (section) params.set('section', section);
    const res = await fetch(`${CMS_PUBLIC}/content?${params}`);
    const data = await res.json();
    return data.content || {};
}

export async function fetchListItems<T = any>(slug: string, page = 0, limit = 50): Promise<T[]> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const res = await fetch(`${CMS_PUBLIC}/list/${slug}?${params}`);
    const data = await res.json();
    return data.response?.items || [];
}

// --- Write (admin — Access protected via CF cookie + CMS secret) ---

export async function saveField(page: string, section: string, field: string, value: string, fieldType = 'text'): Promise<void> {
    await fetch(`${CMS_ADMIN}/content`, {
        method: 'PUT',
        headers: adminHeaders(),

        body: JSON.stringify({ page, section, field, value, field_type: fieldType }),
    });
}

export async function saveBulkFields(fields: Array<{ page: string; section: string; field: string; value: string; field_type?: string }>): Promise<void> {
    const res = await fetch(`${CMS_ADMIN}/content/bulk`, {
        method: 'PUT',
        headers: adminHeaders(),

        body: JSON.stringify({ fields }),
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Publish failed (${res.status})`);
    }
}

export async function addListItem(slug: string, data: Record<string, any>, sortOrder = 0): Promise<number> {
    const res = await fetch(`${CMS_ADMIN}/list/${slug}`, {
        method: 'POST',
        headers: adminHeaders(),

        body: JSON.stringify({ data, sort_order: sortOrder }),
    });
    const json = await res.json();
    return json.id;
}

export async function bulkImportListItems(slug: string, items: Array<Record<string, any>>): Promise<number> {
    const res = await fetch(`${CMS_ADMIN}/list/${slug}/bulk`, {
        method: 'POST',
        headers: adminHeaders(),

        body: JSON.stringify({ items }),
    });
    const data = await res.json();
    return data.imported || 0;
}

export async function updateListItem(slug: string, id: number, data: Record<string, any>, sortOrder?: number): Promise<void> {
    const body: any = { data };
    if (sortOrder !== undefined) body.sort_order = sortOrder;
    await fetch(`${CMS_ADMIN}/list/${slug}/${id}`, {
        method: 'PUT',
        headers: adminHeaders(),

        body: JSON.stringify(body),
    });
}

export async function deleteListItem(slug: string, id: number): Promise<void> {
    await fetch(`${CMS_ADMIN}/list/${slug}/${id}`, {
        method: 'DELETE',
        headers: adminHeaders(),

    });
}

export async function reorderList(slug: string, ids: number[]): Promise<void> {
    await fetch(`${CMS_ADMIN}/list/${slug}/reorder`, {
        method: 'PUT',
        headers: adminHeaders(),

        body: JSON.stringify({ ids }),
    });
}

export async function uploadMedia(file: File, altText = ''): Promise<{ id: number; r2_key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt_text', altText);

    const h: Record<string, string> = {};
    const token = getCmsToken();
    if (token) h['x-cms-token'] = token;
    if (CF_ACCESS_CLIENT_ID) h['CF-Access-Client-Id'] = CF_ACCESS_CLIENT_ID;
    if (CF_ACCESS_CLIENT_SECRET) h['CF-Access-Client-Secret'] = CF_ACCESS_CLIENT_SECRET;

    const res = await fetch(`${CMS_ADMIN}/media`, {
        method: 'POST',
        headers: h,

        body: formData,
    });
    const data = await res.json();
    return data.media;
}

export async function fetchVersions(page?: string, section?: string, field?: string, limit = 20): Promise<any[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (page) params.set('page', page);
    if (section) params.set('section', section);
    if (field) params.set('field', field);
    const res = await fetch(`${CMS_ADMIN}/versions?${params}`, {
        headers: adminHeaders(),

    });
    const data = await res.json();
    return data.versions || [];
}

export async function restoreVersion(versionId: number): Promise<void> {
    await fetch(`${CMS_ADMIN}/versions/restore`, {
        method: 'POST',
        headers: adminHeaders(),

        body: JSON.stringify({ version_id: versionId }),
    });
}

export async function verifyAdmin(): Promise<{ is_admin: boolean; role?: string }> {
    const res = await fetch(`${CMS_ADMIN}/auth/verify`, {
        method: 'POST',
        headers: adminHeaders(),

    });
    if (res.status === 302 || res.redirected) {
        // Cloudflare Access hasn't been completed yet
        return { is_admin: false };
    }
    return res.json();
}

// The URL to redirect the user to for Cloudflare Access authentication
// After Access auth, the worker redirects back to `return` param with cfauth marker
export function getAccessLoginUrl(): string {
    const returnUrl = encodeURIComponent(window.location.origin + window.location.pathname + '?cfauth=1');
    return `${CMS_ADMIN}/auth/verify?return=${returnUrl}`;
}
